import { ulBriefcase01 } from '@faceup/icons/ulBriefcase01'
import { ulBuilding07 } from '@faceup/icons/ulBuilding07'
import { ulBuilding08 } from '@faceup/icons/ulBuilding08'
import { ulDataflow02 } from '@faceup/icons/ulDataflow02'
import { ulFolder } from '@faceup/icons/ulFolder'
import { ulGraduationHat02 } from '@faceup/icons/ulGraduationHat02'
import { ulLogOut04 } from '@faceup/icons/ulLogOut04'
import { ulMail01 } from '@faceup/icons/ulMail01'
import { ulPhoneCall01 } from '@faceup/icons/ulPhoneCall01'
import { ulPhoneCall02 } from '@faceup/icons/ulPhoneCall02'
import { ulPhoneIncoming01 } from '@faceup/icons/ulPhoneIncoming01'
import { ulShieldTick } from '@faceup/icons/ulShieldTick'
import { ulStars02 } from '@faceup/icons/ulStars02'
import { ulTable } from '@faceup/icons/ulTable'
import { ulTool01 } from '@faceup/icons/ulTool01'
import { ulUsers01 } from '@faceup/icons/ulUsers01'
import {
  AdminMenu,
  type AdminMenuItem,
  type AdminMenuLogo,
  PageContentSkeleton,
  AdminLayout as UiAdminLayout,
} from '@faceup/ui'
import { convertRegionToRegionName, SupportedRegion } from '@faceup/utils'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { routes } from '../hooks/useRoutes'
import { canAccess } from '../utils/fuAdminAccess'
import { DEFAULT_SUPPORTED_REGION, useRegion } from '../utils/useRegion'
import { RegionHeader } from './RegionHeader'

const regionCodes: Record<SupportedRegion, string> = {
  [SupportedRegion.UsWest1]: 'US',
  [SupportedRegion.EuWest1]: 'EU',
  [SupportedRegion.MeCentral1]: 'ME',
  [SupportedRegion.ApSoutheast2]: 'AU',
}

const fragments = {
  FuAdminLayout_admin: graphql(`
    fragment FuAdminLayout_admin on FuAdmin {
      id
      roles
      companyCountries
      schoolCountries
    }
  `),
}

type AdminLayoutProps = {
  children?: React.ReactNode
  logout: () => void
  viewer: FragmentType<typeof fragments.FuAdminLayout_admin> | null
}

export const AdminLayout = ({ viewer: _viewer, logout, children }: AdminLayoutProps) => {
  const viewer = getFragmentData(fragments.FuAdminLayout_admin, _viewer)
  const { region } = useRegion()
  const activeRegion = region ?? DEFAULT_SUPPORTED_REGION
  const logo: AdminMenuLogo = {
    src: '/logo/U-logo.svg',
    name: 'Kredenc',
    backgroundColor: '#fd9f13',
    link: routes => routes.home(),
    badge: regionCodes[activeRegion],
    caption: convertRegionToRegionName(activeRegion),
  }
  const roles = viewer?.roles ?? []

  const footerItems: AdminMenuItem[] = [
    {
      key: 'log-out',
      label: 'Log out',
      icon: ulLogOut04,
      onClick: logout,
    },
  ]

  // mock loading instead of spinner
  if (!viewer?.id) {
    return (
      <UiAdminLayout
        menu={
          <AdminMenu label='Main navigation' logo={logo} items={[]} footerItems={footerItems} />
        }
      >
        <RegionHeader />
        <PageContentSkeleton />
      </UiAdminLayout>
    )
  }

  const items: AdminMenuItem[] = [
    {
      key: 'organizations',
      label: 'Organizations',
      icon: ulBuilding07,
      secondary: {
        sections: [
          {
            key: 'organizations-institutions',
            items: [
              {
                key: 'companies',
                label: 'Companies',
                icon: ulBuilding08,
                link: routes => routes.companies(),
                match: [routes.institution],
                visible: Boolean(viewer.companyCountries?.length) && canAccess(roles, 'companies'),
              },
              {
                key: 'schools',
                label: 'Schools',
                icon: ulGraduationHat02,
                link: routes => routes.schools(),
                match: [routes.institution],
                visible: Boolean(viewer.schoolCountries?.length) && canAccess(roles, 'schools'),
              },
              {
                key: 'partners',
                label: 'Partners',
                icon: ulUsers01,
                link: routes => routes.partners(),
                match: [routes.partner],
                visible: Boolean(viewer.companyCountries?.length) && canAccess(roles, 'partners'),
              },
            ],
          },
        ],
      },
    },
    {
      key: 'cases',
      label: 'Cases',
      icon: ulBriefcase01,
      link: routes => routes.reports(),
      match: [routes.report],
      visible: canAccess(roles, 'cases'),
    },
    {
      key: 'materials',
      label: 'Materials',
      icon: ulFolder,
      link: routes => routes.materials(),
      visible: canAccess(roles, 'materials'),
    },
    {
      key: 'ai-prompts',
      label: 'AI prompts',
      icon: ulStars02,
      link: routes => routes.aiPrompts(),
      visible: canAccess(roles, 'aiPrompts'),
    },
    {
      key: 'hotlines',
      label: 'Hotlines',
      icon: ulPhoneCall01,
      secondary: {
        sections: [
          {
            key: 'hotlines-configuration',
            items: [
              {
                key: 'hotlines-list',
                label: 'Hotlines',
                icon: ulPhoneIncoming01,
                link: routes => routes.hotlines(),
                visible: canAccess(roles, 'hotlines'),
              },
              {
                key: 'vonage-hotlines',
                label: 'Vonage hotlines',
                icon: ulPhoneCall02,
                link: routes => routes.vonageHotlines(),
                visible: canAccess(roles, 'vonageHotlines'),
              },
            ],
          },
        ],
      },
    },
    {
      key: 'tools',
      label: 'Tools',
      icon: ulTool01,
      secondary: {
        sections: [
          {
            key: 'tools-internal',
            items: [
              {
                key: 'send-emails',
                label: 'Send emails',
                icon: ulMail01,
                link: routes => routes.sendEmails(),
                visible: canAccess(roles, 'sendEmails'),
              },
              {
                key: 'region-maintenance',
                label: 'Region maintenance',
                icon: ulTable,
                link: routes => routes.regionMaintenance(),
                visible: canAccess(roles, 'regionMaintenance'),
              },
              {
                key: 'integration-http-logs',
                label: 'Integration HTTP logs',
                icon: ulDataflow02,
                link: routes => routes.integrationHttpLogs(),
                visible: canAccess(roles, 'integrationHttpLogs'),
              },
              {
                key: 'internal-users',
                label: 'Internal users',
                icon: ulShieldTick,
                link: routes => routes.internalUsers(),
                visible: canAccess(roles, 'internalUsers'),
              },
            ],
          },
        ],
      },
    },
  ]

  return (
    <UiAdminLayout
      menu={
        <AdminMenu label='Main navigation' logo={logo} items={items} footerItems={footerItems} />
      }
    >
      <RegionHeader />
      {children}
    </UiAdminLayout>
  )
}
